import { assign, createActor, setup } from 'xstate'
import type { ActorOptions, SnapshotFrom } from 'xstate'
import type { ErrorKind } from '../error-kind'
import { AuthorizationRefusedError } from './authorization-error'
import type { BootEvent, BootPhase, BootTier } from './boot-phase'

interface BootFailure {
    tier: BootTier
    kind: ErrorKind
    error: unknown
}

interface BootContext {
    /** Number of transient session retries already started. Zero for the first load. */
    sessionAttempt: number
    failure: BootFailure | undefined
    /** True from a restored session until boot settles again. The app stays mounted meanwhile. */
    restoring: boolean
}

/** Transient session failures are retried this many times before boot fails. */
export const MAX_SESSION_RETRIES = 3

/** Delay before retry number `attempt` (zero based): 1s, 2s, 4s. */
export const sessionRetryDelayMs = (attempt: number): number => 1000 * 2 ** attempt

const failureOf =
    (tier: BootTier) =>
    ({ event }: { event: BootEvent }): BootFailure | undefined =>
        'kind' in event ? { tier, kind: event.kind, error: event.error } : undefined

const errorOf = (event: BootEvent): unknown => ('error' in event ? event.error : undefined)

const kindOf = (event: BootEvent): ErrorKind | undefined => ('kind' in event ? event.kind : undefined)

/**
 * The boot phase machine. It performs no I/O: loaders report what happened as events and read the
 * phase. `sessionBackoff` is internal and reports as the `session` phase; the loader keys its
 * effect on (phase, attempt) so a retry starts exactly when `selectSessionAttempt` increments.
 */
export const bootMachine = setup({
    types: {
        context: {} as BootContext,
        events: {} as BootEvent,
    },
    guards: {
        canRetrySession: ({ context, event }) =>
            kindOf(event) === 'transient' && context.sessionAttempt < MAX_SESSION_RETRIES,
        // A forbidden session check is an invalid session and signs in again. A forbidden identity-provider
        // refusal is the user's answer: it stays on the failed screen, since signing in again would ask the
        // provider the same question.
        sessionRejected: ({ event }) =>
            kindOf(event) === 'unauthenticated' ||
            (kindOf(event) === 'forbidden' && !(errorOf(event) instanceof AuthorizationRefusedError)),
        // A 403 from an access call can be a real permission refusal, so only 401 means the session is gone.
        accessSessionEnded: ({ event }) => kindOf(event) === 'unauthenticated',
        // A canceled load was superseded by a newer one, which reports its own outcome; it never fails boot.
        loadFailed: ({ event }) => kindOf(event) !== 'aborted',
        failedIdentity: ({ context }) => context.failure?.tier === 'identity',
        failedSession: ({ context }) => context.failure?.tier === 'session',
        failedAccess: ({ context }) => context.failure?.tier === 'access',
    },
    delays: {
        sessionBackoff: ({ context }) => sessionRetryDelayMs(context.sessionAttempt),
    },
    actions: {
        failIdentity: assign({ failure: failureOf('identity') }),
        failSession: assign({ failure: failureOf('session') }),
        failAccess: assign({ failure: failureOf('access') }),
        clearFailure: assign({ failure: undefined }),
        resetAttempt: assign({ sessionAttempt: 0 }),
        startRestore: assign({ restoring: true }),
        endRestore: assign({ restoring: false }),
        nextAttempt: assign({ sessionAttempt: ({ context }) => context.sessionAttempt + 1 }),
    },
}).createMachine({
    id: 'boot',
    initial: 'identity',
    context: { sessionAttempt: 0, failure: undefined, restoring: false },
    states: {
        identity: {
            on: {
                identityLoaded: 'session',
                identityFailed: { guard: 'loadFailed', target: 'failed', actions: 'failIdentity' },
            },
        },
        session: {
            on: {
                sessionValidated: { target: 'access', actions: 'resetAttempt' },
                sessionMissing: 'unauthenticated',
                sessionFailed: [
                    { guard: 'sessionRejected', target: 'unauthenticated' },
                    { guard: 'canRetrySession', target: 'sessionBackoff' },
                    // Transient with retries spent, and misconfigured: a retry here will not help.
                    { guard: 'loadFailed', target: 'failed', actions: 'failSession' },
                ],
            },
        },
        sessionBackoff: {
            after: {
                sessionBackoff: { target: 'session', actions: 'nextAttempt' },
            },
        },
        access: {
            on: {
                accessLoaded: 'ready',
                accessFailed: [
                    { guard: 'accessSessionEnded', target: 'unauthenticated' },
                    { guard: 'loadFailed', target: 'failed', actions: 'failAccess' },
                ],
            },
        },
        ready: {
            entry: 'endRestore',
            on: {
                sessionExpired: 'expired',
                loggedOut: 'unauthenticated',
            },
        },
        expired: {
            on: {
                sessionRestored: { target: 'session', actions: 'startRestore' },
                loggedOut: 'unauthenticated',
            },
        },
        unauthenticated: {
            entry: 'endRestore',
            on: {
                // The sign-out request failed, so the session is still live.
                logoutFailed: 'ready',
            },
        },
        failed: {
            entry: 'endRestore',
            on: {
                retried: [
                    { guard: 'failedIdentity', target: 'identity', actions: 'clearFailure' },
                    { guard: 'failedSession', target: 'session', actions: ['clearFailure', 'resetAttempt'] },
                    { guard: 'failedAccess', target: 'access', actions: 'clearFailure' },
                ],
            },
        },
    },
})

export type BootSnapshot = SnapshotFrom<typeof bootMachine>

/** Creates a started-by-caller boot actor. Pass `clock` to run on simulated time. */
export const createBootActor = (options?: ActorOptions<typeof bootMachine>) => createActor(bootMachine, options)

/** Projects a snapshot onto the public phase shape. */
export function toBootPhase(snapshot: BootSnapshot): BootPhase {
    switch (snapshot.value) {
        case 'identity':
        case 'access':
        case 'ready':
        case 'unauthenticated':
        case 'expired':
            return { phase: snapshot.value }
        case 'session':
        case 'sessionBackoff':
            return { phase: 'session' }
        case 'failed': {
            const failure = snapshot.context.failure
            // `failed` is only entered with a failure recorded; an unreachable fallback must not look like success.
            return failure
                ? { phase: 'failed', tier: failure.tier, kind: failure.kind, error: failure.error }
                : { phase: 'failed', tier: 'identity', kind: 'misconfigured', error: undefined }
        }
    }
}

/** Zero for the first session load, then 1, 2, 3 for each transient retry. */
export const selectSessionAttempt = (snapshot: BootSnapshot): number => snapshot.context.sessionAttempt

/** True while a restored session is validated again; the app stays mounted for it. */
export const selectRestoring = (snapshot: BootSnapshot): boolean => snapshot.context.restoring
